/**
 * Data Export Card - Export CRM data to CSV files
 */

import {
  Building07,
  CurrencyDollar,
  Upload01,
  User01,
} from '@untitledui/icons';
import type { FC } from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import { useCrmExport } from '@/hooks/crm/useCrmExport';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';

type IconComponent = FC<{ className?: string }>;

interface ExportRowProps {
  icon: IconComponent;
  title: string;
  description: string;
  buttonLabel: string;
  isExporting: boolean;
  onExport: () => Promise<void>;
}

function ExportRow({
  icon,
  title,
  description,
  buttonLabel,
  isExporting,
  onExport,
}: ExportRowProps) {
  const handleExport = useCallback(async () => {
    try {
      await onExport();
      toast.success(`${title} exported successfully`);
    } catch {
      toast.error(`We couldn't export ${title.toLowerCase()}`);
    }
  }, [onExport, title]);

  return (
    <ButtonList.Item
      trailing={
        <Button
          size='sm'
          color='secondary'
          iconLeading={Upload01}
          onClick={handleExport}
          isLoading={isExporting}
          isDisabled={isExporting}
        >
          {buttonLabel}
        </Button>
      }
    >
      <div className='flex items-start gap-4'>
        <FeaturedIcon icon={icon} size='md' theme='modern' color='gray' />
        <div className='min-w-0'>
          <h3 className='text-sm font-semibold text-fg-primary'>{title}</h3>
          <p className='mt-1 text-sm text-fg-tertiary'>{description}</p>
        </div>
      </div>
    </ButtonList.Item>
  );
}

export function DataExportCard() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? 0;

  const {
    exportContacts,
    exportCompanies,
    exportDeals,
    exportingContacts,
    exportingCompanies,
    exportingDeals,
  } = useCrmExport(orgId);

  return (
    <ButtonList aria-label='Data export'>
      <ExportRow
        icon={User01}
        title='Contacts'
        description='Export all contacts including names, emails, phone numbers, and custom properties.'
        buttonLabel='Export contacts'
        isExporting={exportingContacts}
        onExport={exportContacts}
      />

      <ExportRow
        icon={Building07}
        title='Companies'
        description='Export all companies including names, domains, and custom properties.'
        buttonLabel='Export companies'
        isExporting={exportingCompanies}
        onExport={exportCompanies}
      />

      <ExportRow
        icon={CurrencyDollar}
        title='Deals'
        description='Export all deals including names, amounts, stages, and custom properties.'
        buttonLabel='Export deals'
        isExporting={exportingDeals}
        onExport={exportDeals}
      />
    </ButtonList>
  );
}
